import { useMemo, useCallback } from 'react';
import { useIsDemo } from '@/hooks/common/useIsDemo';
import { useSelectedOrgId } from '@/hooks/common/useSelectedOrgId';
import {
  demoDeals,
  demoDocuments,
  demoContractsData,
  demoActionItems,
  demoDealStages,
  demoParticipants,
  getDemoDealById as rawGetDemoDealById,
  getDemoContractById as rawGetDemoContractById,
} from '@/mockData/demoData';
import { DEMO_CONTRACT_IDS } from '@/mockData/demoContractData';
import { ALL_DEALS, PERSONAL_ORG_ID } from '@/mockData';
import { ARI_PIPELINE_DEALS, ARI_SUGGESTED_DEALS, mapAriDealToDemoDeal } from '@/mockData/ariPipeline';
import { filterByOrg } from '@/mockData/common/demoFilters';
import type { DemoDeal, DemoContract } from '@/mockData/demoData';

function formatDealValue(value: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(value);
}

function mapStageToStatus(stage: string): DemoDeal['status'] {
  switch (stage) {
    case 'complete':
      return 'Completed';
    case 'signing':
      return 'Pending';
    case 'delivery':
      return 'Pending';
    case 'in_progress':
      return 'In Progress';
    case 'lead':
    default:
      return 'Draft';
  }
}

function getCoreContractId(type: string): string | undefined {
  const normalized = type.toLowerCase();

  if (
    normalized.includes('record') ||
    normalized.includes('amendment') ||
    normalized.includes('distribution') ||
    normalized.includes('catalog')
  ) {
    return DEMO_CONTRACT_IDS.ARTIST_RECORDING_DEAL;
  }

  if (normalized.includes('producer')) {
    return DEMO_CONTRACT_IDS.PRODUCER_AGREEMENT;
  }

  if (
    normalized.includes('brand') ||
    normalized.includes('merch') ||
    normalized.includes('sync')
  ) {
    return DEMO_CONTRACT_IDS.BRAND_PARTNERSHIP_NIL;
  }

  if (normalized.includes('joint venture') || normalized.includes('venture')) {
    return DEMO_CONTRACT_IDS.SAFE_AGREEMENT;
  }

  return undefined;
}

export function useDemoDealsDocuments() {
  const isDemo = useIsDemo();
  const selectedOrgId = useSelectedOrgId();
  const activeOrgId = selectedOrgId || PERSONAL_ORG_ID;

  const deals = useMemo(() => {
    if (!isDemo) {
      return [];
    }

    if (activeOrgId === PERSONAL_ORG_ID) {
      const ariDeals = [...ARI_PIPELINE_DEALS, ...ARI_SUGGESTED_DEALS].map(mapAriDealToDemoDeal);
      const ariIds = new Set(ariDeals.map((d) => d.id));
      return [...ariDeals, ...demoDeals.filter((d) => !ariIds.has(d.id))];
    }

    return filterByOrg(ALL_DEALS, activeOrgId).map((deal) => ({
      id: deal.id,
      name: deal.name,
      type: deal.type,
      status: mapStageToStatus(deal.stage),
      value: formatDealValue(deal.value),
      date: deal.date,
      updatedAt: deal.lastActivity,
      documentCount: deal.documentsCount,
      coreDocument: `${deal.type} Agreement`,
      coreContractId: getCoreContractId(deal.type),
    }));
  }, [activeOrgId, isDemo]);
  const documents = useMemo(() => (isDemo ? demoDocuments : []), [isDemo]);
  const contracts = useMemo(
    () => (isDemo ? demoContractsData : []),
    [isDemo],
  );
  const actionItems = useMemo(
    () => (isDemo ? demoActionItems : []),
    [isDemo],
  );
  const dealStages = useMemo(
    () => (isDemo ? demoDealStages : []),
    [isDemo],
  );
  const participants = useMemo(
    () => (isDemo ? demoParticipants : []),
    [isDemo],
  );
  const demoContractIds = useMemo(
    () => (isDemo ? DEMO_CONTRACT_IDS : ({} as typeof DEMO_CONTRACT_IDS)),
    [isDemo],
  );

  const getDemoDealById = useCallback(
    (id: string): DemoDeal | undefined => {
      if (!isDemo) {
        return undefined;
      }

      return deals.find((deal) => deal.id === id) ?? rawGetDemoDealById(id);
    },
    [deals, isDemo],
  );

  const getDemoContractById = useCallback(
    (id: string): DemoContract | undefined =>
      isDemo ? rawGetDemoContractById(id) : undefined,
    [isDemo],
  );

  return {
    demoDeals: deals,
    demoDocuments: documents,
    demoContracts: contracts,
    demoActionItems: actionItems,
    demoDealStages: dealStages,
    demoParticipants: participants,
    demoContractIds,
    getDemoDealById,
    getDemoContractById,
  };
}
